import { Injectable, Injector, NgZone, inject } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BehaviorSubject, Observable } from 'rxjs';
import { API, MAPBOX_TOKEN } from '../../../environments/environment';

// Services
import { MapboxService } from './mapbox.service';
import { MapboxLayersService } from './mapbox-layers.service';
import { MapboxClusterService } from './mapbox-cluster.service';
import { MapboxDrawService } from './mapbox-draw.service';
import { EntityManagerRegistry } from './core/registry/entity-manager-registry.service';
import { HelperService } from '../servicios/helper.service';
import { LoginService } from '../../modulos/login/login.service';
import { ObtenerDataEntidadService } from '../mapa-facil/obtener-data-entidad.component';

// Types
import { TipoDatosPopup } from '../mapa-facil/interfaces';
import { ILayerMapaPersonalizado } from 'modelos/src';

// Managers
import {
  ALL_ENTITY_MANAGER_CLASSES,
  PolylinesLayerManager,
  PolygonsLayerManager,
  HeatmapLayerManager,
} from './entity-managers';

// Tipos
import { TipoMapaBase } from '../../modulos/standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { aplicarEtiquetasMapa } from './etiquetas-mapa';

//Datos para actualizar el mapa (estructura simplificada)
export interface MapaFacilV2Data {
  // Entidades por tipo
  vehiculos?: any[];
  colectivos?: any[];
  trackers?: any[];
  activos?: any[];
  luminarias?: any[];
  alarmas?: any[];
  alertasSeguridad?: any[];
  eventosSeguridad?: any[];
  sirenas?: any[];
  gateways?: any[];
  paradas?: any[];
  puntos?: any[];
  emergenciasMedicas?: any[];
  hospitales?: any[];
  ambulancias?: any[];
  centrosAtencion?: any[];

  // Geometrías
  polylines?: any[];
  polygons?: any[];
  heatmap?: {
    points: [number, number][];
    radius?: number;
    intensity?: number;
    opacity?: number;
    gradient?: string[];
  };

  // Configuración
  center?: [number, number];
  zoom?: number;
  clustering?: {
    [entityType: string]: {
      enabled: boolean;
    };
  };
}

//Configuración del mapa
export interface MapaFacilV2Config {
  container: HTMLElement | string;
  style?: TipoMapaBase;
  center?: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  disablePopups?: boolean; // Deshabilitar popups (útil para mapas pequeños de detalle)
  cooperativeGestures?: boolean; // Requiere Ctrl/Cmd + scroll para zoom (útil para mapas en formularios/detalles)
  mapId?: string; // Identificador único para namespacing de cache por mapa
}

/**
 * MapaFacilV2Service
 *
 * Nueva versión del servicio de mapas que usa SOLO managers.
 * Reemplaza completamente a MapaFacilService y openLayers.service.
 *
 * Características:
 * - 100% basado en managers (sin código legacy)
 * - API simplificada e intuitiva
 * - Registro automático de todos los managers
 * - Gestión automática de lifecycle
 * - Type-safe con TypeScript
 *
 * Uso:
 * ```typescript
 * const map = await this.mapaFacilV2.createMap({
 *   container: 'map-container',
 *   center: [-58.443832, -34.587386],
 *   zoom: 12
 * });
 *
 * this.mapaFacilV2.updateData({
 *   vehiculos: vehiculosArray,
 *   luminarias: luminariasArray,
 *   polylines: recorridosArray
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class MapaFacilV2Service {
  private mapboxService = inject(MapboxService);
  private layersService = inject(MapboxLayersService);
  private clusterService = inject(MapboxClusterService);
  private drawService = inject(MapboxDrawService);
  private entityRegistry = inject(EntityManagerRegistry);
  private injector = inject(Injector);
  private helper = inject(HelperService);
  private dataEntidadService = inject(ObtenerDataEntidadService);
  private ngZone = inject(NgZone);

  // ========== MAPA ==========
  private map: mapboxgl.Map | null = null;
  private isMapReady = false;
  private popupsDisabled = false; // Flag para deshabilitar popups en mapas pequeños

  // ========== BEHAVIOR SUBJECTS ==========
  private mapReadySubject = new BehaviorSubject<boolean>(false);
  public mapReady$: Observable<boolean> = this.mapReadySubject.asObservable();

  // ========== CACHE DE DATOS ==========
  private cachedData: MapaFacilV2Data = {};
  private cachedDataByMapId: Record<string, MapaFacilV2Data> = {};
  private currentMapId: string | null = null;

  // ========== POPUP STATE (Observables para OnPush) ==========
  private popupVisibleSubject = new BehaviorSubject<boolean>(false);
  private popupDatosSubject = new BehaviorSubject<any>(null);
  private popupTituloSubject = new BehaviorSubject<TipoDatosPopup | undefined>(
    undefined,
  );
  private popupCoordinateSubject = new BehaviorSubject<[number, number] | null>(
    null,
  );

  // Observables públicos (para usar con | async en templates)
  public popupVisible$ = this.popupVisibleSubject.asObservable();
  public popupDatos$ = this.popupDatosSubject.asObservable();
  public popupTitulo$ = this.popupTituloSubject.asObservable();
  public popupCoordinate$ = this.popupCoordinateSubject.asObservable();

  // Referencias para el popup overlay
  private currentPopup: mapboxgl.Popup | null = null;
  private popupElement: HTMLElement | null = null;

  // Cleanup de los listeners de re-inicialización del cambio de estilo pendiente.
  // Permite cancelar una re-inicialización aún no disparada (ej: el usuario cambia
  // de capa base otra vez antes de que la anterior termine de cargar) y evitar que
  // listeners viejos crucen y re-inicialicen contra el mapa equivocado.
  private styleReadyCleanup: (() => void) | null = null;

  // Generación del estilo actual. Se incrementa en cada setMapStyle().
  // El doReinit() es async (await loadAllIcons): entre que arranca y termina, el
  // usuario puede disparar otro switch. Sin este contador, la continuación vieja
  // corría initializeAllManagers() contra un estilo ya reemplazado / a medio cargar
  // → addSource lanzaba "Style is not done loading" y los markers no aparecían.
  private styleEpoch = 0;

  // ========== MANAGERS DE GEOMETRÍAS ==========
  private polylinesManager: PolylinesLayerManager | null = null;
  private polygonsManager: PolygonsLayerManager | null = null;
  private heatmapManager: HeatmapLayerManager | null = null;

  // ========== MAPAS PERSONALIZADOS ==========
  private mapasPersonalizados: ILayerMapaPersonalizado[] = [];

  // ========== OPCIONES DE RE-APLICACIÓN DE DATOS EN CAMBIO DE ESTILO ==========
  // Controla si se re-aplican los datos cacheados al cambiar la capa base
  private reapplyDataOnStyleChange = true;
  // Define qué tipos de entidades se permiten re-aplicar (geometrías siempre)
  private allowedEntityTypes: Set<string> | null = null;
  // Controla si se re-aplican geometrías (polylines/polygons/heatmap) al cambiar la capa base
  private includeGeometriesOnStyleChange = true;

  constructor() {
    // IMPORTANTE: Event listeners de window fuera de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      // Escuchar eventos de configuración del mapa
      window.addEventListener('mapa-3d-toggle', (e: any) => {
        this.actualizarConfiguracion3D(e.detail);
      });

      window.addEventListener('mapa-dia-noche-toggle', (e: any) => {
        this.actualizarConfiguracionDiaNoche(e.detail);
      });

      window.addEventListener('mapa-oscuro-toggle', (e: any) => {
        this.actualizarModoOscuroMapa(e.detail);
      });

      window.addEventListener('mapa-pois-toggle', (e: any) => {
        this.actualizarConfiguracionPOIs(e.detail);
      });

      // window.addEventListener('mapa-traffic-toggle', (e: any) => {
      //   this.actualizarCapaTrafico(e.detail);
      // });
    });
  }

  // ========== CREACIÓN Y CONFIGURACIÓN ==========

  /**
   * Crea y configura un nuevo mapa
   *
   * @param config Configuración del mapa
   * @returns Promise que resuelve cuando el mapa está listo
   */

  async createMap(config: MapaFacilV2Config): Promise<mapboxgl.Map> {
    // Guardar configuración de popups
    this.popupsDisabled = config.disablePopups || false;

    // Seleccionar/establecer el mapId actual y sincronizar el cache de trabajo
    this.currentMapId = config.mapId ?? this.currentMapId ?? 'default';
    const id = this.currentMapId || 'default';
    if (!this.cachedDataByMapId[id]) {
      this.cachedDataByMapId[id] = {};
    }
    // El cachedData activo siempre referencia al namespace del mapa actual
    this.cachedData = this.cachedDataByMapId[id];

    // CRÍTICO: Crear mapa FUERA de Angular Zone para evitar que Mapbox dispare change detection
    // El loop de renderizado interno de Mapbox usa requestAnimationFrame, MessageChannel y Promises
    // que quedarían patcheados por Zone.js si se crean dentro de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      // Crear mapa
      this.map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: config.container,
        style: this.getMapStyle(config.style || 'osm'),
        center: config.center || [-58.443832, -34.587386],
        zoom: config.zoom || 12,
        // Sin fade de símbolos (default 300 ms), igual que el mapa de vehículos:
        // con el fade, cada recolocación de símbolos deja el render en loop
        // animando la opacidad de todos los íconos. Con miles de pines (mapa de
        // alarmas, ~6.000) eso trababa el mapa al moverlo y al alejar el zoom.
        fadeDuration: 0,
        pitch: config.pitch || 0,
        transformRequest: (url: string, resourceType: string) => {
          // Para peticiones a Mapbox API, configurar referrerPolicy para que funcionen las URL restrictions
          if (url.includes('api.mapbox.com')) {
            return {
              url: url,
              referrerPolicy: 'origin-when-cross-origin' as ReferrerPolicy,
            };
          }

          // Para tiles de nuestra API (proxy TomTom), inyectar el token de autenticación
          if (resourceType === 'Tile' && url.includes('/proxy-tiles/tomtom/')) {
            return {
              url,
              headers: {
                Authorization: `Bearer ${LoginService.getAccessToken()}`,
                'x-permiso': LoginService.getIdPermiso(),
              },
            };
          }

          // Para tiles de servidores externos sin CORS, forzar omit
          if (
            resourceType === 'Tile' &&
            !url.startsWith(window.location.origin)
          ) {
            return {
              url: url,
              credentials: 'omit' as any, // Force omit para servidores sin CORS
            };
          }
          return { url };
        },
        bearing: config.bearing || 0,
        // Requiere Ctrl/Cmd + scroll para zoom solo si está habilitado en config
        // Útil para mapas en formularios/detalles, NO para mapas fullscreen
        cooperativeGestures: config.cooperativeGestures || false,
        // Logo abajo a la derecha, junto a la atribución: la esquina izquierda
        // queda para el selector de mapa base (el logo tiene que verse)
        logoPosition: 'bottom-right',
        // La atribución va compacta (botón "i"); se agrega abajo como control
        attributionControl: false,
        accessToken: MAPBOX_TOKEN,
      });
      aplicarEtiquetasMapa(this.map);

      // Atribución compacta: obligatoria (Mapbox y OpenStreetMap), pero como
      // botón "i" que muestra el texto sólo al tocarlo
      this.map.addControl(
        new mapboxgl.AttributionControl({ compact: true }),
        'bottom-right',
      );

      // Agregar controles de navegación
      this.map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );
    });

    // IMPORTANTE: Configurar ciclo día/noche para Mapbox Standard cuando el estilo se carga
    // Ejecutar event listener fuera de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      this.map!.on('style.load', () => {
        this.configurarCicloDiaNoche(this.map!, config.style || 'osm');

        // Deshabilitar terrain 3D para evitar errores 403 con terrain-dem tiles
        // Esto no afecta los edificios 3D que son parte del estilo
        this.map!.setTerrain(null);
      });
    });

    // Esperar evento 'load'
    return new Promise((resolve) => {
      this.ngZone.runOutsideAngular(() => {
        this.map!.on('load', async () => {
          // Cargar todos los iconos
          await this.layersService.loadAllIcons(this.map!);

          // Re-vincular managers al mapa activo (importante cuando se abren mini-mapas/dialogs)
          this.rebindManagersToCurrentMap();

          // Inicializar TODOS los entity managers automáticamente
          this.initializeAllManagers();

          // CRÍTICO: Volver a entrar en Angular Zone para actualizar estado y resolver promesa
          this.ngZone.run(() => {
            // Marcar como listo
            this.isMapReady = true;
            this.mapReadySubject.next(true);
            resolve(this.map!);
          });
        });
      });
    });
  }

  // Obtiene la instancia del mapa
  getMap(): mapboxgl.Map | null {
    return this.map;
  }

  //Verifica si el mapa está listo
  isReady(): boolean {
    return this.isMapReady;
  }

  // ========== INICIALIZACIÓN DE MANAGERS ==========

  /**
   * Re-vincula el estado interno de managers al mapa activo.
   * Evita que los managers queden apuntando al mapa anterior cuando se abre
   * un mini-mapa (por ejemplo, en dialogs de detalle).
   */
  private rebindManagersToCurrentMap(): void {
    // Resetear registry para que permita initializeAll sobre el mapa actual
    this.entityRegistry.resetAll();

    // Resetear managers de geometrías (singletons) para que initialize() no retorne temprano
    if (this.polylinesManager) {
      (this.polylinesManager as any).isInitialized = false;
      (this.polylinesManager as any).map = null;
    }
    if (this.polygonsManager) {
      (this.polygonsManager as any).isInitialized = false;
      (this.polygonsManager as any).map = null;
    }
    if (this.heatmapManager) {
      (this.heatmapManager as any).isInitialized = false;
      (this.heatmapManager as any).map = null;
    }
  }

  //Inicializa automáticamente TODOS los managers
  private initializeAllManagers(): void {
    if (!this.map) return;

    // IMPORTANTE: Inicializar geometry managers PRIMERO para que queden en el fondo
    // En Mapbox, layers agregados primero aparecen ABAJO, layers agregados después aparecen ARRIBA
    // Orden visual deseado (de abajo hacia arriba):
    //   1. Geometrías (polylines, polygons, heatmap) ← FONDO
    //   2. Entidades (colectivos, activos, alarmas, etc.) ← ARRIBA
    this.polylinesManager = this.injector.get(PolylinesLayerManager);
    this.polylinesManager.initialize(this.map);

    this.polygonsManager = this.injector.get(PolygonsLayerManager);
    this.polygonsManager.initialize(this.map);

    this.heatmapManager = this.injector.get(HeatmapLayerManager);
    this.heatmapManager.initialize(this.map);

    // Registrar todos los entity managers automáticamente
    ALL_ENTITY_MANAGER_CLASSES.forEach((ManagerClass: any) => {
      const manager = this.injector.get(ManagerClass) as any;
      this.entityRegistry.register(manager);
    });

    // Inicializar todos los entity managers con callback de popup (solo si no están deshabilitados)
    this.entityRegistry.initializeAll(
      this.map,
      this.layersService,
      this.clusterService,
      this.popupsDisabled
        ? undefined
        : (datos, tipo, coordinate) =>
            this.mostrarPopup(datos, tipo, coordinate),
      this.ngZone,
    );

    // this.addTrafficLayer();
  }

  // ========== ACTUALIZACIÓN DE DATOS ==========

  /**
   * Actualiza los datos del mapa
   *
   * API simplificada: pasa un objeto con los datos de cada tipo de entidad
   *
   * @param data Datos a actualizar
   */
  updateData(data: MapaFacilV2Data): void {
    if (!this.isMapReady) {
      return;
    }

    // IMPORTANTE: Guardar datos en cache para poder re-aplicarlos al cambiar estilo
    this.cachedData = { ...this.cachedData, ...data };
    const id = this.currentMapId || 'default';
    this.cachedDataByMapId[id] = {
      ...(this.cachedDataByMapId[id] || {}),
      ...data,
    };

    // Actualizar entidades usando el registry
    const entitiesToUpdate = new Map<string, any[]>();

    if (data.vehiculos) entitiesToUpdate.set('vehiculos', data.vehiculos);
    if (data.colectivos) entitiesToUpdate.set('colectivos', data.colectivos);
    if (data.trackers) entitiesToUpdate.set('trackers', data.trackers);
    if (data.activos) entitiesToUpdate.set('activos', data.activos);
    if (data.luminarias) entitiesToUpdate.set('luminarias', data.luminarias);
    if (data.alarmas) entitiesToUpdate.set('alarmas', data.alarmas);
    if (data.alertasSeguridad)
      entitiesToUpdate.set('alertasSeguridad', data.alertasSeguridad);
    if (data.eventosSeguridad)
      entitiesToUpdate.set('eventosSeguridad', data.eventosSeguridad);
    if (data.sirenas) entitiesToUpdate.set('sirenas', data.sirenas);
    if (data.gateways) entitiesToUpdate.set('gateways', data.gateways);
    if (data.paradas) entitiesToUpdate.set('paradas', data.paradas);
    if (data.puntos) entitiesToUpdate.set('puntos', data.puntos);
    if (data.emergenciasMedicas)
      entitiesToUpdate.set('emergenciasMedicas', data.emergenciasMedicas);
    if (data.hospitales) entitiesToUpdate.set('hospitales', data.hospitales);
    if (data.ambulancias) entitiesToUpdate.set('ambulancias', data.ambulancias);
    if (data.centrosAtencion)
      entitiesToUpdate.set('centrosAtencion', data.centrosAtencion);

    // Batch update de entidades
    this.entityRegistry.updateDataBatch(entitiesToUpdate);

    // Actualizar geometrías
    if (data.polylines && this.polylinesManager) {
      this.polylinesManager.updatePolylines(data.polylines);
    }

    if (data.polygons && this.polygonsManager) {
      this.polygonsManager.updatePolygons(data.polygons);
    }

    if (data.heatmap && this.heatmapManager) {
      this.heatmapManager.updateHeatmap(data.heatmap);
    }

    // Aplicar configuración de clustering
    if (data.clustering) {
      this.updateClustering(data.clustering);
    }

    // Centrar mapa si se especificó
    if (data.center && this.map) {
      this.map.setCenter(data.center);
    }

    // Ajustar zoom si se especificó
    if (data.zoom && this.map) {
      this.map.setZoom(data.zoom);
    }
  }

  /**
   * Actualiza datos del mapa con debounce (agrupa múltiples llamadas)
   *
   * IMPORTANTE: Útil cuando se reciben updates frecuentes (polling cada 1-5s, websockets).
   * Agrupa múltiples llamadas en una sola actualización para mejorar performance.
   *
   * @param data Datos a actualizar
   * @param debounceMs Milisegundos de espera antes de actualizar (default: 150ms)
   *
   * @example
   * // Polling cada 2 segundos - agrupa updates
   * setInterval(() => {
   *   this.vehiculosService.getAll().subscribe(vehiculos => {
   *     this.mapaService.updateDataDebounced({ vehiculos }, 150);
   *   });
   * }, 2000);
   */
  updateDataDebounced(data: MapaFacilV2Data, debounceMs: number = 150): void {
    if (!this.isMapReady) {
      return;
    }

    // IMPORTANTE: Guardar datos en cache
    this.cachedData = { ...this.cachedData, ...data };
    const id = this.currentMapId || 'default';
    this.cachedDataByMapId[id] = {
      ...(this.cachedDataByMapId[id] || {}),
      ...data,
    };

    // Actualizar entidades usando updateDataDebounced
    if (data.vehiculos)
      this.entityRegistry
        .get('vehiculos')
        ?.updateDataDebounced(data.vehiculos, debounceMs);
    if (data.colectivos)
      this.entityRegistry
        .get('colectivos')
        ?.updateDataDebounced(data.colectivos, debounceMs);
    if (data.trackers)
      this.entityRegistry
        .get('trackers')
        ?.updateDataDebounced(data.trackers, debounceMs);
    if (data.activos)
      this.entityRegistry
        .get('activos')
        ?.updateDataDebounced(data.activos, debounceMs);
    if (data.luminarias)
      this.entityRegistry
        .get('luminarias')
        ?.updateDataDebounced(data.luminarias, debounceMs);
    if (data.alarmas)
      this.entityRegistry
        .get('alarmas')
        ?.updateDataDebounced(data.alarmas, debounceMs);
    if (data.alertasSeguridad)
      this.entityRegistry
        .get('alertasSeguridad')
        ?.updateDataDebounced(data.alertasSeguridad, debounceMs);
    if (data.eventosSeguridad)
      this.entityRegistry
        .get('eventosSeguridad')
        ?.updateDataDebounced(data.eventosSeguridad, debounceMs);
    if (data.sirenas)
      this.entityRegistry
        .get('sirenas')
        ?.updateDataDebounced(data.sirenas, debounceMs);
    if (data.gateways)
      this.entityRegistry
        .get('gateways')
        ?.updateDataDebounced(data.gateways, debounceMs);
    if (data.paradas)
      this.entityRegistry
        .get('paradas')
        ?.updateDataDebounced(data.paradas, debounceMs);
    if (data.puntos)
      this.entityRegistry
        .get('puntos')
        ?.updateDataDebounced(data.puntos, debounceMs);
    if (data.emergenciasMedicas)
      this.entityRegistry
        .get('emergenciasMedicas')
        ?.updateDataDebounced(data.emergenciasMedicas, debounceMs);
    if (data.hospitales)
      this.entityRegistry
        .get('hospitales')
        ?.updateDataDebounced(data.hospitales, debounceMs);
    if (data.ambulancias)
      this.entityRegistry
        .get('ambulancias')
        ?.updateDataDebounced(data.ambulancias, debounceMs);
    if (data.centrosAtencion)
      this.entityRegistry
        .get('centrosAtencion')
        ?.updateDataDebounced(data.centrosAtencion, debounceMs);

    // Actualizar geometrías (sin debounce, generalmente no se actualizan tan frecuentemente)
    if (data.polylines && this.polylinesManager) {
      this.polylinesManager.updatePolylines(data.polylines);
    }
    if (data.polygons && this.polygonsManager) {
      this.polygonsManager.updatePolygons(data.polygons);
    }
    if (data.heatmap && this.heatmapManager) {
      this.heatmapManager.updateHeatmap(data.heatmap);
    }

    // Aplicar configuración de clustering
    if (data.clustering) {
      this.updateClustering(data.clustering);
    }

    // Centrar mapa si se especificó
    if (data.center && this.map) {
      this.map.setCenter(data.center);
    }

    // Ajustar zoom si se especificó
    if (data.zoom && this.map) {
      this.map.setZoom(data.zoom);
    }
  }

  //Limpia todos los datos del mapa (sin destruir layers)
  clearAllData(): void {
    this.entityRegistry.clearAllData();

    if (this.polylinesManager) {
      this.polylinesManager.clearPolylines();
    }

    if (this.polygonsManager) {
      this.polygonsManager.clearPolygons();
    }

    if (this.heatmapManager) {
      this.heatmapManager.clearHeatmap();
    }
  }

  //Limpia el caché de datos

  //IMPORTANTE: Usar en mapas pequeños (detalles/modals) para evitar que sus datos
  //se mezclen con el mapa general al cambiar de estilo.
  clearCache(): void {
    const id = this.currentMapId || 'default';
    this.cachedData = {};
    this.cachedDataByMapId[id] = {};
  }

  // ========== CLUSTERING ==========

  //Actualiza la configuración de clustering
  private updateClustering(clusteringConfig: {
    [entityType: string]: { enabled: boolean };
  }): void {
    const clusteringMap = new Map<string, boolean>();

    Object.entries(clusteringConfig).forEach(([entityType, config]) => {
      clusteringMap.set(entityType, config.enabled);
    });
  }

  //Habilita/deshabilita clustering para un tipo de entidad
  setClusteringEnabled(entityType: string, enabled: boolean): void {
    const manager = this.entityRegistry.get(entityType);

    if (manager && manager.supportsClustering) {
      manager.setClusteringEnabled(enabled);
    }
  }

  /**
   * Habilita/deshabilita mostrar nombres de markers para un tipo de entidad
   *
   * @param entityType Tipo de entidad ('vehiculos', 'luminarias', etc.)
   * @param enabled true para mostrar nombres, false para ocultarlos
   *
   * @example
   * // Mostrar nombres de vehículos
   * mapaService.setMostrarNombresMarkers('vehiculos', true);
   *
   * // Ocultar nombres de vehículos
   * mapaService.setMostrarNombresMarkers('vehiculos', false);
   */
  setMostrarNombresMarkers(entityType: string, enabled: boolean): void {
    const manager = this.entityRegistry.get(entityType);

    if (manager && typeof manager.setOptions === 'function') {
      manager.setOptions({ mostrarNombresMarkers: enabled });
    }
  }

  // ========== FILTRADO ==========

  /**
   * Aplica un filtro a un tipo de entidad específico
   *
   * IMPORTANTE: El filtro se aplica directamente en Mapbox sin regenerar GeoJSON.
   * Esto es 70-90% más rápido que filtrar el array y llamar updateData().
   *
   * @param entityType Tipo de entidad ('alarmas', 'luminarias', etc.)
   * @param filterExpression Expresión de filtro de Mapbox
   *
   * @example
   * // Filtrar alarmas activas
   * mapaService.setFilter('alarmas', ['==', ['get', 'estado'], 'Activa']);
   *
   * // Filtrar luminarias con error
   * mapaService.setFilter('luminarias', ['in', ['get', 'estado'], ['literal', ['Error', 'Apagada']]]);
   */
  setFilter(entityType: string, filterExpression: mapboxgl.Expression): void {
    const manager = this.entityRegistry.get(entityType);
    if (manager) {
      manager.setFilter(filterExpression);
    } else {
      // console.warn(`⚠️ No se encontró manager para tipo: ${entityType}`);
    }
  }

  /**
   * Elimina el filtro de un tipo de entidad, mostrando todas las entidades
   *
   * @param entityType Tipo de entidad ('alarmas', 'luminarias', etc.)
   */
  clearFilter(entityType: string): void {
    const manager = this.entityRegistry.get(entityType);
    if (manager) {
      manager.clearFilter();
    }
  }

  /**
   * Obtiene el filtro actual de un tipo de entidad
   *
   * @param entityType Tipo de entidad ('alarmas', 'luminarias', etc.)
   * @returns Expresión de filtro actual o null si no hay filtro
   */
  getCurrentFilter(entityType: string): mapboxgl.Expression | null {
    const manager = this.entityRegistry.get(entityType);
    return manager ? manager.getCurrentFilter() : null;
  }

  // ========== VISIBILIDAD ==========

  // Controla la visibilidad de un tipo de entidad
  setEntityVisibility(entityType: string, visible: boolean): void {
    const manager = this.entityRegistry.get(entityType);
    if (manager) {
      manager.setVisibility(visible);
    }
  }

  // ========== POPUP ==========

  //Muestra el popup con datos
  //Los datos vienen generalmente sólo con el valor de la ubicación, luego se hace fetch para obtener datos completos.
  //IMPORTANTE: a los datos de cada entidad se le pueden agregar campos desde los entity-managers.
  //Estos campos que se agregan con la función getEntityForPopup, pueden ser usados para diferenciar popups de la misma entidad.
  //Ej: se puede agregar desde el entity manager de alarmas un campo que sea mapaEspecifico e indicar si viene del mapa de alarmas o el mapa general, y usar esto para luego mostrar popups diferentes en cada mapa
  //MUESTRA EL POPUP INMEDIATAMENTE con loading, luego actualiza con datos completos
  async mostrarPopup(
    datos: any,
    tipo: string,
    coordinate?: [number, number],
  ): Promise<void> {
    const tipoNormalizado = this.normalizarTipoParaPopup(tipo, datos);

    const idEntidad = datos._id || datos.id;
    const needsFetch = idEntidad && this.needsDataFetch(datos, tipo);

    // MOSTRAR POPUP INMEDIATAMENTE (antes del fetch)
    this.popupTituloSubject.next(tipoNormalizado);
    this.popupCoordinateSubject.next(coordinate || null);
    this.popupVisibleSubject.next(true);

    if (needsFetch) {
      // Mostrar loading: enviamos data: null para que el popup muestre su loading existente
      this.popupDatosSubject.next({
        tipo: tipoNormalizado,
        data: null, // null para que muestre loading
        coordinates: coordinate,
      });

      // Crear overlay con popup en loading
      if (coordinate) {
        setTimeout(() => this.crearOverlayPopup(coordinate), 0);
      }

      // Hacer fetch en background
      try {
        const datosObtenidos =
          await this.dataEntidadService.obtenerEntidadPorId(
            tipoNormalizado,
            idEntidad,
          );
        if (datosObtenidos) {
          // Actualizar con datos completos
          // IMPORTANTE: Si ambos objetos tienen propiedades anidadas (como datosPopup, popupData, idsAsignados, etc),
          // hacer merge manual para no perder campos agregados por los entity managers
          const mergedData = { ...datos, ...datosObtenidos };

          // Merge manual de propiedades anidadas comunes que pueden venir de ambos lados
          if (datos.popupData && datosObtenidos.popupData) {
            mergedData.popupData = {
              ...datos.popupData,
              ...datosObtenidos.popupData,
            };
          }

          this.popupDatosSubject.next({
            tipo: tipoNormalizado,
            data: mergedData,
            coordinates: coordinate,
          });
        } else {
          this.popupDatosSubject.next({
            tipo: tipoNormalizado,
            data: datos,
            coordinates: coordinate,
          });
        }
      } catch (error) {
        console.error(
          `❌ Error al obtener datos de ${tipoNormalizado}:`,
          error,
        );
        // Mostrar datos originales en caso de error
        this.popupDatosSubject.next({
          tipo: tipoNormalizado,
          data: datos,
          coordinates: coordinate,
        });
      }
    } else {
      // No necesita fetch, mostrar datos directamente (sin loading)
      this.popupDatosSubject.next({
        tipo: tipoNormalizado,
        data: datos,
        coordinates: coordinate,
      });

      // Crear overlay
      if (coordinate) {
        setTimeout(() => this.crearOverlayPopup(coordinate), 0);
      }
    }
  }

  // Determina si necesitamos hacer fetch de datos completos
  //Verifica si el objeto tiene propiedades populadas
  private needsDataFetch(datos: any, tipo: string): boolean {
    // Para reportes (vehiculos, colectivos, activos, trackers), SIEMPRE hacer fetch
    // porque en mapa general vienen con select mínimo (solo valores.geojson)
    if (
      [
        'vehiculos',
        'colectivos',
        'activos',
        'trackers',
        'vehiculo',
        'colectivo',
        'activo',
        'tracker',
      ].includes(tipo)
    ) {
      // Si NO tiene activo populado completo (no solo el ID), hacer fetch
      return (
        !datos.activo ||
        typeof datos.activo === 'string' ||
        !datos.activo.identificacion
      );
    }

    // Para luminarias, SIEMPRE hacer fetch porque pueden venir solo con ubicacion
    // (sin otros datos necesarios para el popup completo)
    if (['luminarias', 'luminaria', 'sirena', 'sirenas'].includes(tipo)) {
      return true;
    }

    // Para alarmas, SIEMPRE hacer fetch porque vienen con domicilio populate mínimo
    // (solo geojson + domicilio, sin el resto de propiedades)
    if (['alarmas', 'alarma'].includes(tipo)) {
      return true;
    }

    // Para alertas de seguridad, SIEMPRE hacer fetch: el listado del mapa
    // viene con select mínimo, el popup necesita la alerta completa
    if (['alertasSeguridad', 'alertaSeguridad'].includes(tipo)) {
      return true;
    }

    // Por defecto, no hacer fetch (para entidades que no necesitan populate)
    return false;
  }

  //Normaliza el tipo de entidad de plural a singular para el template del popup
  private normalizarTipoParaPopup(tipo: string, datos: any): TipoDatosPopup {
    // Los centros de atención son siempre bases de operaciones médicas
    // (Emergencias Bomberos se dio de baja).
    const tipoNormalizado =
      tipo === 'centrosAtencion' ? 'centrosMedicos' : tipo;
    const pluralToSingular: Record<string, TipoDatosPopup> = {
      vehiculos: 'vehiculo',
      colectivos: 'colectivo',
      trackers: 'tracker',
      activos: 'activo',
      luminarias: 'luminaria',
      alarmas: 'alarma',
      alertasSeguridad: 'alertaSeguridad',
      eventosSeguridad: 'eventoSeguridad',
      sirenas: 'sirena',
      gateways: 'gateway',
      paradas: 'parada',
      emergenciasMedicas: 'emergenciaMedica',
      ambulancias: 'ambulancia',
      hospitales: 'hospital',
      centrosMedicos: 'centroMedico',
    };

    return (
      pluralToSingular[tipoNormalizado] || (tipoNormalizado as TipoDatosPopup)
    );
  }

  // Cierra el popup
  cerrarPopup(): void {
    // Usar BehaviorSubjects para notificar cambios (compatible con OnPush)
    this.popupVisibleSubject.next(false);
    this.popupDatosSubject.next(null);
    this.popupTituloSubject.next(undefined);
    this.popupCoordinateSubject.next(null);

    // Cerrar el popup de Mapbox
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    // Limpiar referencias - el elemento será recreado por Angular cuando popupVisible === true
    this.popupElement = null;
  }

  //Crea y posiciona el popup overlay en el mapa
  // Reintenta si el elemento no está disponible (Angular necesita tiempo para renderizar)

  private crearOverlayPopup(
    coordinate: [number, number],
    intentos: number = 0,
  ): void {
    const MAX_INTENTOS = 5;
    const DELAY_MS = 50;

    // Usar getElementById para obtener el contenedor del popup
    const element = document.getElementById('mapa-facil-v2-popup-container');

    if (!element) {
      // Si el elemento no existe y aún tenemos intentos, reintentar
      if (intentos < MAX_INTENTOS) {
        setTimeout(() => {
          this.crearOverlayPopup(coordinate, intentos + 1);
        }, DELAY_MS);
        return;
      }

      console.warn(
        '⚠️ Contenedor del popup no encontrado en el DOM después de',
        MAX_INTENTOS,
        'intentos',
      );
      return;
    }

    // Inicializar o actualizar el popup overlay
    this.initPopupOverlay(element);

    // Posicionar el popup
    this.showPopup(coordinate);
  }

  //Inicializa el popup overlay con mapboxgl.Popup
  private initPopupOverlay(element: HTMLElement): void {
    if (!this.map) {
      console.warn('⚠️ Mapa no disponible para inicializar popup overlay');
      return;
    }

    // IMPORTANTE: Siempre actualizar la referencia al elemento
    const elementoNuevo = !this.popupElement || this.popupElement !== element;
    this.popupElement = element;

    // IMPORTANTE: Siempre cerrar popup anterior
    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    // Crear un Popup de Mapbox con el elemento HTML del componente Angular
    this.currentPopup = new mapboxgl.Popup({
      closeButton: false, // El componente Angular tiene su propio botón de cerrar
      closeOnClick: false, // No cerrar al hacer click en el mapa
      anchor: 'bottom', // El borde inferior del popup está en el punto
      offset: [0, -15], // Separación de 15px hacia arriba desde el marcador
      maxWidth: 'none', // IMPORTANTE: Sin ancho máximo para permitir que el componente use su propio ancho (320px o 550px)
      className: 'mapbox-popup-angular', // Clase personalizada
    }).setDOMContent(element); // Usar el elemento HTML del componente Angular
  }

  // Muestra el popup en las coordenadas especificadas

  private showPopup(coordinate: [number, number]): void {
    if (!this.currentPopup || !this.map) {
      return;
    }

    try {
      // Posicionar y mostrar el popup en las coordenadas
      this.currentPopup.setLngLat(coordinate).addTo(this.map);

      // Ajustar la vista del mapa para que el popup sea completamente visible
      this.adjustMapViewForPopup(coordinate);
    } catch (error) {
      console.error('⚠️ Error al mostrar popup:', error);
      // Si falla, limpiar referencias para forzar reinicialización
      this.currentPopup = null;
      this.popupElement = null;
    }
  }

  /**
   * Ajusta la vista del mapa para que el popup sea completamente visible
   * Solo mueve el mapa si el marker está en la zona superior (tapado por filtros)
   * IMPORTANTE: Compatible con mapas inclinados (pitch > 0)
   *
   * Usa padding de Mapbox cuando necesita mover, evitando el problema de
   * project()/unproject() que no son lineales en proyección 3D.
   */
  private adjustMapViewForPopup(coordinate: [number, number]): void {
    if (!this.map) return;

    // Detectar si el marker está en la zona problemática (superior)
    const markerPoint = this.map.project(coordinate);
    const mapHeight = this.map.getContainer().clientHeight;
    const markerVerticalPosition = markerPoint.y / mapHeight;

    // Si el marker está en el 45% superior (zona tapada por filtros + popup),
    // mover el mapa para que quede visible
    // Si está más abajo, no hacer nada (ya es visible)
    if (markerVerticalPosition < 0.45) {
      const topPadding = mapHeight * 0.35; // 35% para filtros + espacio del popup
      const bottomPadding = mapHeight * 0.1; // 10% de margen inferior

      // Usar padding en easeTo() para que Mapbox maneje la geometría 3D
      this.map.easeTo({
        center: coordinate,
        padding: {
          top: topPadding,
          bottom: bottomPadding,
          left: 50,
          right: 50,
        },
        duration: 300,
        easing: (t) => t * (2 - t), // ease-out
      });
    }
    // Si markerVerticalPosition >= 0.45, el marker ya está visible, no hacer nada
  }

  //Oculta todas las entidades
  hideAllEntities(): void {
    this.entityRegistry.hideAll();
  }

  // Muestra todas las entidades
  showAllEntities(): void {
    this.entityRegistry.showAll();
  }

  // ========== DRAWING / EDICIÓN ==========

  //Habilita el modo de dibujo
  enableDrawing(): void {
    if (!this.map) return;
    this.drawService.initDrawControls(this.map);
  }

  //Obtiene el servicio de dibujo
  getDrawService(): MapboxDrawService {
    return this.drawService;
  }

  // ========== HELPERS DE ESTILO ==========

  //Obtiene el estilo de mapa base
  private getMapStyle(tipo: TipoMapaBase): string | mapboxgl.Style {
    // Detectar mapas personalizados (formato: personalizado_0, personalizado_1, etc.)
    if (tipo.startsWith('personalizado_')) {
      const index = parseInt(tipo.replace('personalizado_', ''));
      const mapaPersonalizado = this.mapasPersonalizados[index];

      if (mapaPersonalizado?.url) {
        return this.createCustomTileStyle(mapaPersonalizado);
      }
    }

    // Estilos Mapbox Standard (v3): se envuelven en un style raíz que IMPORTA el
    // basemap y declara `glyphs`. El Standard cargado por URL directa NO expone un
    // `glyphs` raíz a las capas que agregamos, así que toda capa con `text-field`
    // (ej: *-cluster-count-layer) falla al validar y aborta el createLayers del
    // manager antes de la capa de iconos → mapa sin markers en Standard/Satélite.
    // El import id 'basemap' mantiene compatible a configurarCicloDiaNoche
    // (setConfigProperty('basemap', ...)).
    if (tipo === 'osm' || tipo === 'satelite') {
      const importUrl =
        tipo === 'satelite'
          ? 'mapbox://styles/mapbox/standard-satellite'
          : 'mapbox://styles/mapbox/standard';
      return this.buildStandardStyle(importUrl);
    }

    // Estilos clásicos (v11): ya traen `glyphs` en su raíz.
    const styles: Record<TipoMapaBase, string> = {
      osm: 'mapbox://styles/mapbox/standard',
      satelite: 'mapbox://styles/mapbox/standard-satellite',
      cartodb: this.helper.mapaOscuroEnabled
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11',
    };

    return styles[tipo] || styles['cartodb'];
  }

  //Envuelve un estilo Mapbox Standard (v3) en un style raíz con `glyphs`/`sprite`
  //e import 'basemap', para poder agregarle capas con texto sin romper la validación.
  private buildStandardStyle(importUrl: string): mapboxgl.Style {
    return {
      version: 8,
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      sprite: 'mapbox://sprites/mapbox/streets-v12',
      imports: [{ id: 'basemap', url: importUrl }],
      sources: {},
      layers: [],
    } as unknown as mapboxgl.Style;
  }

  //Configura el ciclo día/noche para el estilo Mapbox Standard
  //Basado en la hora local, establece el preset de iluminación
  private configurarCicloDiaNoche(
    map: mapboxgl.Map,
    tipoMapaBase: TipoMapaBase,
  ): void {
    // Solo aplicar si el estilo actual es Standard
    const isStandardStyle = tipoMapaBase === 'osm' || tipoMapaBase === 'google';

    const horaActual = new Date().getHours();

    if (!isStandardStyle) {
      return;
    }

    // El `tipoMapaBase` puede no ser el estilo cargado: el listener de
    // 'style.load' del init pasa siempre `config.style` (el estilo INICIAL), y
    // también se dispara al cambiar a una capa clásica o personalizada. Sin el
    // import 'basemap', setConfigProperty tira y se veía «❌ Error configurando
    // Mapbox Standard». Se mira el estilo real.
    const tieneBasemap = (map.getStyle()?.imports ?? []).some(
      (imp) => imp.id === 'basemap',
    );
    if (!tieneBasemap) {
      return;
    }

    try {
      // Configurar 3D objects según preferencia del usuario
      const show3dObjects = this.helper.mapa3DEnabled;
      map.setConfigProperty('basemap', 'show3dObjects', show3dObjects);

      // Configurar POIs según preferencia del usuario
      const showPOIs = this.helper.mapaPOIsEnabled;
      map.setConfigProperty('basemap', 'showPointOfInterestLabels', showPOIs);

      // Configurar lightPreset solo si está habilitado el ciclo día/noche
      if (this.helper.mapaDiaNocheEnabled) {
        const lightPreset = this.calcularLightPreset();
        map.setConfigProperty('basemap', 'lightPreset', lightPreset);
      } else {
        // Si está deshabilitado, usar 'day' por defecto
        map.setConfigProperty('basemap', 'lightPreset', 'day');
      }
    } catch (error) {
      console.error('❌ Error configurando Mapbox Standard:', error);
    }
  }

  // Actualiza la configuración de 3D objects en tiempo real
  private actualizarConfiguracion3D(enabled: boolean): void {
    if (!this.map) return;

    try {
      this.map.setConfigProperty('basemap', 'show3dObjects', enabled);
    } catch (error) {
      console.warn(
        '⚠️ No se pudo actualizar 3D objects (puede no ser estilo Standard):',
        error,
      );
    }
  }

  //Actualiza la configuración de día/noche en tiempo real
  private actualizarConfiguracionDiaNoche(enabled: boolean): void {
    if (!this.map) return;

    try {
      const lightPreset = enabled ? this.calcularLightPreset() : 'day';
      this.map.setConfigProperty('basemap', 'lightPreset', lightPreset);
    } catch (error) {
      console.warn(
        '⚠️ No se pudo actualizar lightPreset (puede no ser estilo Standard):',
        error,
      );
    }
  }

  //Actualiza el modo oscuro del mapa minimal en tiempo real
  private actualizarModoOscuroMapa(enabled: boolean): void {
    if (!this.map) return;

    // Solo cambiar si estamos en el mapa 'cartodb' (minimal)
    const currentStyle = this.map.getStyle();
    if (
      currentStyle?.sprite?.includes('light-v11') ||
      currentStyle?.sprite?.includes('dark-v11')
    ) {
      const newStyle = enabled
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11';
      this.setMapStyle('cartodb');
    } else {
    }
  }

  //Actualiza la configuración de POIs en tiempo real
  private actualizarConfiguracionPOIs(enabled: boolean): void {
    if (!this.map) return;

    try {
      this.map.setConfigProperty(
        'basemap',
        'showPointOfInterestLabels',
        enabled,
      );
    } catch (error) {
      console.warn(
        '⚠️ No se pudo actualizar POIs (puede no ser estilo Standard):',
        error,
      );
    }
  }

  private addTrafficLayer(): void {
    if (!this.map || this.map.getSource('tomtom-traffic')) return;

    this.map.addSource('tomtom-traffic', {
      type: 'vector',
      tiles: [`${API}/proxy-tiles/tomtom/traffic/{z}/{x}/{y}.pbf`],
      minzoom: 0,
      maxzoom: 22,
      bounds: [-180, -56, -34, 72], // Continente americano
    });

    this.map.addLayer({
      id: 'traffic-layer',
      type: 'line',
      source: 'tomtom-traffic',
      'source-layer': 'Traffic flow',
      slot: 'middle', // Mapbox standard: debajo de labels, arriba del fondo
      paint: {
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          6,
          1,
          12,
          2.5,
          16,
          4,
        ],
        'line-color': [
          'interpolate',
          ['linear'],
          ['get', 'traffic_level'],
          0,
          '#cc0000', // detenido
          0.3,
          '#ff6600', // lento
          0.6,
          '#ffcc00', // moderado
          0.8,
          '#99cc00', // fluido
          1,
          '#33cc33', // libre
        ],
      },
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        visibility: this.helper.mapaTraficoEnabled ? 'visible' : 'none',
      },
    });
  }

  private actualizarCapaTrafico(enabled: boolean): void {
    if (!this.map || !this.map.getLayer('traffic-layer')) return;
    this.map.setLayoutProperty(
      'traffic-layer',
      'visibility',
      enabled ? 'visible' : 'none',
    );
  }

  /**
   * Calcula el preset de iluminación basado en la hora del día
   * @returns 'dawn' | 'day' | 'dusk' | 'night'
   */
  private calcularLightPreset(): 'dawn' | 'day' | 'dusk' | 'night' {
    const hora = new Date().getHours();

    // Amanecer: 5:00 - 7:59
    if (hora >= 5 && hora < 8) {
      return 'dawn';
    }
    // Día: 8:00 - 17:59
    else if (hora >= 8 && hora < 18) {
      return 'day';
    }
    // Atardecer: 18:00 - 20:59
    else if (hora >= 18 && hora < 21) {
      return 'dusk';
    }
    // Noche: 21:00 - 4:59
    else {
      return 'night';
    }
  }

  /**
   * Detecta si el mapa está en modo oscuro
   * Considera el tipo de mapa base, configuración de modo oscuro y ciclo día/noche
   * @param tipoMapaBase El tipo de mapa base actual
   * @returns true si el mapa está en modo oscuro, false si está en modo claro
   */
  public isMapaDarkMode(tipoMapaBase: TipoMapaBase): boolean {
    // 1. Satelite siempre es oscuro
    if (tipoMapaBase === 'satelite') {
      return true;
    }

    // 2. Cartodb con modo oscuro habilitado
    if (tipoMapaBase === 'cartodb' && this.helper.mapaOscuroEnabled) {
      return true;
    }

    // 3. OSM con ciclo día/noche activo y en horario oscuro (dusk o night)
    if (tipoMapaBase === 'osm' && this.helper.mapaDiaNocheEnabled) {
      const lightPreset = this.calcularLightPreset();
      return lightPreset === 'dusk' || lightPreset === 'night';
    }

    // Caso por defecto: modo claro
    return false;
  }

  //Convierte URLs de servidores externos a URLs del proxy cuando es necesario
  //Esto evita problemas de CORS con servidores que no tienen los headers apropiados

  private convertToProxyUrl(originalUrl: string): string {
    // Configuración de servidores conocidos que requieren proxy
    const proxyMappings = [
      {
        // Servidor de Montevideo
        pattern:
          /geoserver\.montevideo\.gub\.uy\/geowebcache\/service\/tms\/1\.0\.0\/([^@]+)@[^\/]+\/\{z\}\/\{x\}\/\{-?y\}\.png/,
        service: 'montevideo',
        extractLayer: (match: RegExpMatchArray) => match[1], // Usar nombre original sin conversión
      },
    ];

    // Intentar hacer match con cada patrón conocido
    for (const mapping of proxyMappings) {
      const match = originalUrl.match(mapping.pattern);
      if (match) {
        const layerName = mapping.extractLayer(match);

        // Determinar la URL base del API según el ambiente
        const apiBaseUrl = this.getApiBaseUrl();

        // Construir URL del proxy
        return `${apiBaseUrl}/gestion-api-gestion/proxy-tiles/${mapping.service}/${layerName}/{z}/{x}/{y}.png`;
      }
    }

    // Si no es un servidor conocido, retornar la URL original
    return originalUrl;
  }

  //Obtiene la URL base del API según el ambiente
  // NOTA: Siempre usa producción para el proxy de tiles, ya que el backend está desplegado allí
  private getApiBaseUrl(): string {
    // Siempre usar producción (el proxy está desplegado allí)
    return 'https://api.irix.ar';
  }

  /**
   * Crea un estilo personalizado de Mapbox con raster tiles
   */
  private createCustomTileStyle(
    layer: ILayerMapaPersonalizado,
  ): mapboxgl.Style {
    // Detectar si la URL usa formato TMS (con {-y})
    const isTMS = layer.url?.includes('{-y}');

    // Convertir URL a proxy si es necesario (para evitar CORS)
    let tileUrl = this.convertToProxyUrl(layer.url!);

    // Convertir {-y} a {y} para que Mapbox lo entienda
    tileUrl = isTMS ? tileUrl.replace('{-y}', '{y}') : tileUrl;

    return {
      version: 8,
      // glyphs + sprite: sin ellos, cualquier capa agregada con `text-field`
      // (ej: *-cluster-count-layer, *-text-layer) falla la validación con
      // "requires a style glyphs property" y aborta el createLayers del manager
      // ANTES de agregar la capa de iconos → el mapa queda sin markers.
      glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
      sprite: 'mapbox://sprites/mapbox/streets-v12',
      sources: {
        'custom-tiles': {
          type: 'raster',
          tiles: [tileUrl],
          tileSize: 256,
          scheme: isTMS ? 'tms' : 'xyz', // TMS invierte las coordenadas Y
          attribution: layer.nombre || '',
        },
      },
      layers: [
        {
          id: 'custom-tiles-layer',
          type: 'raster',
          source: 'custom-tiles',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    };
  }

  //Establece los mapas personalizados disponibles
  setMapasPersonalizados(mapas: ILayerMapaPersonalizado[]): void {
    this.mapasPersonalizados = mapas;
  }

  /**
   * Controla si se re-aplican los datos cacheados cuando cambia el estilo
   * @param enabled true para re-aplicar (default), false para NO re-aplicar
   */
  public setReapplyDataOnStyleChange(enabled: boolean): void {
    this.reapplyDataOnStyleChange = enabled;
  }

  /**
   * Define el alcance de entidades permitidas para re-aplicar datos en cambio de estilo
   * Pasa null para permitir todas. Las geometrías y configuración (center/zoom/clustering)
   * SIEMPRE se re-aplican.
   */
  public setAllowedEntityTypes(types: string[] | null): void {
    this.allowedEntityTypes = types ? new Set(types) : null;
  }

  /**
   * Obtiene las entidades permitidas para re-aplicación de datos
   */
  public getAllowedEntityTypes(): string[] | null {
    return this.allowedEntityTypes ? Array.from(this.allowedEntityTypes) : null;
  }

  /**
   * Controla si se incluyen las geometrías al re-aplicar datos luego de cambiar el estilo
   */
  public setIncludeGeometriesOnStyleChange(enabled: boolean): void {
    this.includeGeometriesOnStyleChange = enabled;
  }

  /**
   * Cambia el estilo del mapa base
   *
   * IMPORTANTE: Cuando se cambia el estilo con map.setStyle(), Mapbox elimina
   * TODOS los sources y layers personalizados. Por eso necesitamos:
   * 1. Resetear el estado de todos los managers
   * 2. Escuchar el evento 'style.load'
   * 3. Re-cargar los iconos
   * 4. Re-inicializar todos los managers
   * 5. Re-aplicar los datos cacheados
   *
   * //IMPROTANTE: Al reaplicar los datos cacheados, sólo aplicar los permitidos para el mapa en el que nos encontremos.
   */
  setMapStyle(tipo: TipoMapaBase): void {
    if (!this.map) return;

    // Marcar esta generación de estilo. Cualquier doReinit() en vuelo de un switch
    // anterior quedará invalidado (epoch !== this.styleEpoch) y abortará antes de
    // tocar el mapa nuevo.
    const epoch = ++this.styleEpoch;

    // Guardar la posición actual del mapa
    const currentCenter = this.map.getCenter();
    const currentZoom = this.map.getZoom();

    // CRÍTICO: Resetear el estado de todos los managers ANTES de cambiar el estilo
    // Esto permite que se re-inicialicen correctamente después
    this.entityRegistry.resetAll();

    // Resetear geometry managers también
    if (this.polylinesManager) {
      (this.polylinesManager as any).isInitialized = false;
      (this.polylinesManager as any).map = null;
    }
    if (this.polygonsManager) {
      (this.polygonsManager as any).isInitialized = false;
      (this.polygonsManager as any).map = null;
    }
    if (this.heatmapManager) {
      (this.heatmapManager as any).isInitialized = false;
      (this.heatmapManager as any).map = null;
    }

    // Cancelar cualquier re-inicialización pendiente de un cambio de estilo
    // anterior que todavía no se disparó (ej: el usuario cambia de capa base
    // otra vez antes de que la anterior termine de cargar). Sin esto, los
    // listeners viejos sobreviven y cruzan: re-inicializan con el `tipo` viejo
    // contra el mapa nuevo → el otro mapa pierde clicks/clusters/popups.
    if (this.styleReadyCleanup) {
      this.styleReadyCleanup();
      this.styleReadyCleanup = null;
    }

    // Cambiar el estilo (esto ELIMINA todos los sources y layers)
    this.map.setStyle(this.getMapStyle(tipo));

    // Marcar como no listo mientras se carga el nuevo estilo
    this.isMapReady = false;

    // Re-inicializar después de que el nuevo estilo se cargue.
    // Ejecutar fuera de Angular Zone.
    //
    // CRÍTICO: NO depender solo de `once('style.load')`. Ese evento se dispara
    // de forma inconsistente para estilos "inline" (objeto, como las capas base
    // personalizadas raster): Mapbox aplica el cambio como diff sin recargar el
    // estilo, y `style.load` no llega. Resultado: al volver a una capa base
    // personalizada (custom → base → custom) los managers nunca se re-inicializan
    // y no funciona ningún click/popup.
    //
    // Solución robusta: disparar la re-inicialización cuando el estilo esté listo,
    // ya sea por `style.load` (estilos URL/full-reload) o por `idle` (fallback que
    // siempre llega cuando el mapa termina de asentarse), gateado por
    // `isStyleLoaded()` y ejecutado UNA sola vez.
    this.ngZone.runOutsideAngular(() => {
      let reinitDone = false;

      const cleanup = () => {
        this.map?.off('style.load', onReady);
        this.map?.off('styledata', onReady);
        this.map?.off('idle', onReady);
      };

      const doReinit = async () => {
        // Abortar si este callback pertenece a un switch ya superado.
        if (reinitDone || !this.map || epoch !== this.styleEpoch) return;
        reinitDone = true;
        cleanup();
        this.styleReadyCleanup = null;

        // IMPORTANTE: Configurar ciclo día/noche para Mapbox Standard
        this.configurarCicloDiaNoche(this.map, tipo);

        // Restaurar posición
        this.map.jumpTo({
          center: currentCenter,
          zoom: currentZoom,
        });

        // Si otro switch ya reemplazó el estilo, no cargar iconos: correría contra
        // un estilo en pleno rebuild y ensuciaría la consola con errores de addImage
        // ("already exists" / "Cannot read ... 'send'").
        if (epoch !== this.styleEpoch) return;

        // Re-cargar iconos
        await this.layersService.loadAllIcons(this.map);

        // Tras el await pudo entrar otro setMapStyle: si esta generación quedó
        // obsoleta, salir ANTES de crear sources/layers (evita el crash
        // "Style is not done loading" contra el estilo entrante).
        if (epoch !== this.styleEpoch) return;

        // Re-inicializar managers (incluye addTrafficLayer)
        this.initializeAllManagers();

        // CRÍTICO: Volver a entrar en Angular Zone para actualizar estado
        this.ngZone.run(() => {
          // Marcar como listo
          this.isMapReady = true;

          // Re-aplicar datos cacheados (según flags)
          if (
            this.reapplyDataOnStyleChange &&
            Object.keys(
              this.cachedDataByMapId[this.currentMapId || 'default'] || {},
            ).length > 0
          ) {
            const baseData =
              this.cachedDataByMapId[this.currentMapId || 'default'] || {};
            const dataToApply = this.allowedEntityTypes
              ? this.filterDataByAllowedTypes(baseData)
              : baseData;
            this.updateData(dataToApply);
          }
        });
      };

      const onReady = () => {
        // Gate por isStyleLoaded(): durante la transición vale false; solo pasa a
        // true cuando el NUEVO estilo quedó realmente cargado y usable para
        // addSource/addLayer. Así evitamos re-inicializar contra un estilo a medio
        // cargar o contra el estilo viejo.
        if (this.map && this.map.isStyleLoaded()) {
          void doReinit();
        }
      };

      // Registrar el cleanup para poder cancelar esta re-inicialización si el
      // usuario vuelve a cambiar de capa antes de que se dispare.
      this.styleReadyCleanup = cleanup;

      // `style.load`: estilos URL / full-reload.
      // `styledata`: se dispara (async) mientras el estilo se instala; gateado por
      //   isStyleLoaded() re-inicializa apenas el nuevo estilo queda usable
      //   (caso diff de capa personalizada, donde `style.load` NO llega).
      // `idle`: backstop garantizado cuando el mapa termina de asentarse.
      //
      // NO llamar onReady() sincrónicamente acá: justo después de setStyle,
      // isStyleLoaded() puede devolver true reflejando el estilo VIEJO (o uno a
      // medio cambiar), disparando una re-inicialización prematura que el estilo
      // entrante borra después → el mapa destino queda sin capas interactivas.
      this.map!.on('style.load', onReady);
      this.map!.on('styledata', onReady);
      this.map!.on('idle', onReady);
    });
  }

  //Filtra los datos cacheados según las entidades permitidas, preservando geometrías y configuraciones
  private filterDataByAllowedTypes(data: MapaFacilV2Data): MapaFacilV2Data {
    if (!this.allowedEntityTypes) return data;

    const alwaysInclude = new Set(['center', 'zoom', 'clustering']);

    const geometryKeys = new Set(['polylines', 'polygons', 'heatmap']);

    const result: any = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) return;
      const isGeometry = geometryKeys.has(key);
      const includeKey =
        alwaysInclude.has(key) ||
        this.allowedEntityTypes!.has(key) ||
        (isGeometry && this.includeGeometriesOnStyleChange);
      if (includeKey) {
        result[key] = value;
      }
    });

    return result as MapaFacilV2Data;
  }

  // ========== LIMPIEZA ==========

  //Destruye completamente el mapa y limpia todos los recursos
  // IMPORTANTE: Llamar en ngOnDestroy
  destroy(): void {
    // Cerrar popup si existe
    this.cerrarPopup();

    const mapToDestroy = this.map;

    // Limpiar solo los managers que apuntan a ESTE mapa.
    // Si los managers siguen apuntando al mapa de fondo (porque un mini-mapa en un dialog
    // no pudo re-inicializarlos al estar ya inicializados), no los tocamos.
    this.entityRegistry.cleanupAll(mapToDestroy ?? undefined);

    // Limpiar geometry managers solo si el mapa es el mismo que se va a destruir
    if (this.polylinesManager) {
      this.polylinesManager.cleanup();
      this.polylinesManager = null;
    }

    if (this.polygonsManager) {
      this.polygonsManager.cleanup();
      this.polygonsManager = null;
    }

    if (this.heatmapManager) {
      this.heatmapManager.cleanup();
      this.heatmapManager = null;
    }

    // Limpiar draw service
    this.drawService.cleanup();

    // Destruir mapa
    if (mapToDestroy) {
      mapToDestroy.remove();
      this.map = null;
    }

    // Limpiar referencias del popup
    this.currentPopup = null;
    this.popupElement = null;

    this.isMapReady = false;
    this.mapReadySubject.next(false);
  }

  /**
   * Restaura el servicio apuntando a un mapa existente (ej: el mapa de fondo
   * después de que un mini-mapa de dialog destruyó el estado del servicio).
   * Solo aplica si el servicio no está listo ya.
   */
  restore(existingMap: mapboxgl.Map): void {
    if (this.isMapReady && this.map === existingMap) return;

    this.map = existingMap;

    // El mapa principal siempre tiene popups habilitados; el mini-mapa del dialog
    // pudo haber seteado popupsDisabled=true en el servicio singleton, así que lo reseteamos.
    this.popupsDisabled = false;

    // Re-vincular managers al mapa restaurado para que updateData vuelva a dibujar
    this.rebindManagersToCurrentMap();
    this.initializeAllManagers();

    this.isMapReady = true;
    this.mapReadySubject.next(true);
  }

  // ========== DEBUG ==========

  //Imprime información de debug del registry
  debugRegistry(): void {
    this.entityRegistry.debugPrint();
  }

  //Obtiene el estado del registry
  getRegistryStatus() {
    return this.entityRegistry.getStatus();
  }
}
